import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Info,
  MoreHorizontal,
  Plus,
  Trophy,
  UserPlus,
  XCircle,
} from 'lucide-react';
import {
  QUOTATION_STATUSES,
  QUOTATION_STATUS_LABELS,
  REQUISITION_PRIORITY_LABELS,
  SUPPLIER_QUOTE_STATUS_LABELS,
  formatCents,
  formatQty,
  type Paginated,
  type Quotation,
  type QuotationStatus,
  type QuotationSummary,
  type Requisition,
  type RequisitionTimelineEntry,
  type SupplierQuote,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank, formatDate, formatDateTime } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SummaryCards } from '@/components/SummaryCards';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField, StatTiles } from '@/components/ContextPanel';
import { Attachments } from '@/components/Attachments';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Drawer, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { MoneyInput, QtyInput } from '@/components/MoneyInput';
import { EmptyState, ErrorState, LoadingState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { useCostCenters, useSuppliers, todayISO } from '@/lib/queries';
import { DateInput } from '@/components/DateInput';

const STATUS_TONE: Record<QuotationStatus, BadgeKind> = {
  RASCUNHO: 'mute',
  AGUARDANDO_PROPOSTAS: 'info',
  AGUARDANDO_ANALISE: 'warn',
  EM_APROVACAO: 'warn',
  FINALIZADA: 'ok',
  CANCELADA: 'mute',
};

/** Iniciais do fornecedor, para a coluna compacta da lista. */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter((p) => p.length > 2);
  return (partes[0]?.[0] ?? '') + (partes[1]?.[0] ?? partes[0]?.[1] ?? '');
}

/**
 * As ações do fluxo são a mesma chamada com outro caminho.
 *
 * Um hook nomeado em vez de função auxiliar dentro do componente: é o que o
 * React exige para a ordem das chamadas ser a mesma entre renders.
 */
function useAcao(id: string | null, caminho: string, onSuccess: () => void) {
  return useMutation({
    mutationFn: (body: unknown) => api.post(`/quotations/${id}/${caminho}`, body ?? {}),
    onSuccess,
  });
}

/**
 * Cotações — a pergunta é "por que este fornecedor e não o outro?".
 *
 * A tela existe para responder isso: o menor preço de cada linha vem
 * destacado, quem não respondeu aparece como não tendo respondido, e escolher
 * fora do menor preço EXIGE justificativa — regra do servidor, repetida aqui
 * em texto para o operador saber antes de clicar.
 */
export function CotacoesPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [requisitionId, setRequisitionId] = useState('');
  const [personId, setPersonId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [awarded, setAwarded] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [checked, setChecked] = useState<string[]>([]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [convidando, setConvidando] = useState(false);
  const [propondo, setPropondo] = useState<SupplierQuote | null>(null);
  const [escolhendo, setEscolhendo] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [semResposta, setSemResposta] = useState<SupplierQuote | null>(null);

  const filtros = {
    search: search || undefined,
    status: status || undefined,
    requisitionId: requisitionId || undefined,
    personId: personId || undefined,
    costCenterId: costCenterId || undefined,
    from: from || undefined,
    to: to || undefined,
    awarded: awarded || undefined,
  };

  const listQuery = useQuery({
    queryKey: ['quotations', { ...filtros, page, perPage }],
    queryFn: () => api.get<Paginated<Quotation>>('/quotations', { ...filtros, page, perPage }),
  });

  const summaryQuery = useQuery({
    queryKey: ['quotations', 'summary', filtros],
    queryFn: () => api.get<QuotationSummary>('/quotations/summary', filtros),
  });

  const detailQuery = useQuery({
    queryKey: ['quotation', selectedId],
    queryFn: () => api.get<Quotation>(`/quotations/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['quotations'] });
    void queryClient.invalidateQueries({ queryKey: ['quotation'] });
    void queryClient.invalidateQueries({ queryKey: ['purchase-requisitions'] });
  };

  const finalizeMutation = useAcao(selectedId, 'finalize', refresh);
  const approveMutation = useAcao(selectedId, 'approve', refresh);
  const cancelMutation = useAcao(selectedId, 'cancel', refresh);

  const answerMutation = useMutation({
    mutationFn: (payload: { id: string; status: string; reason: string | null }) =>
      api.post(`/quotations/${selectedId}/suppliers/${payload.id}/answer`, {
        status: payload.status,
        reason: payload.reason,
      }),
    onSuccess: () => {
      setSemResposta(null);
      refresh();
    },
  });

  const quotation = detailQuery.data;

  const maisFiltrosAtivos = [requisitionId, personId, costCenterId, from, to, awarded].filter(Boolean).length;
  const temFiltro = Boolean(search || status) || maisFiltrosAtivos > 0;

  const limparMais = () => {
    setRequisitionId('');
    setPersonId('');
    setCostCenterId('');
    setFrom('');
    setTo('');
    setAwarded('');
    setPage(1);
  };
  const limparTudo = () => {
    setSearch('');
    setStatus('');
    limparMais();
  };

  const resumo = summaryQuery.data;
  const cards = [
    {
      key: 'andamento',
      label: 'Em andamento',
      value: String(resumo?.inProgress ?? 0),
      hint: 'Cotações abertas',
      tone: 'info' as const,
      active: status === 'AGUARDANDO_PROPOSTAS',
      onClick: () => {
        setStatus(status === 'AGUARDANDO_PROPOSTAS' ? '' : 'AGUARDANDO_PROPOSTAS');
        setPage(1);
      },
    },
    {
      key: 'analise',
      label: 'Aguardando análise',
      value: String(resumo?.waitingAnalysis ?? 0),
      hint: 'Propostas recebidas',
      tone: 'atencao' as const,
      active: status === 'AGUARDANDO_ANALISE',
      onClick: () => {
        setStatus(status === 'AGUARDANDO_ANALISE' ? '' : 'AGUARDANDO_ANALISE');
        setPage(1);
      },
    },
    {
      key: 'aprovacao',
      label: 'Em aprovação',
      value: String(resumo?.inApproval ?? 0),
      hint: 'Acima da alçada da requisição',
      tone: 'atencao' as const,
      active: status === 'EM_APROVACAO',
      onClick: () => {
        setStatus(status === 'EM_APROVACAO' ? '' : 'EM_APROVACAO');
        setPage(1);
      },
    },
    {
      key: 'finalizadas',
      label: 'Finalizadas',
      value: String(resumo?.finished ?? 0),
      hint: 'Com vencedor definido',
      tone: 'ok' as const,
      active: status === 'FINALIZADA',
      onClick: () => {
        setStatus(status === 'FINALIZADA' ? '' : 'FINALIZADA');
        setPage(1);
      },
    },
    {
      key: 'economia',
      label: 'Economia estimada',
      value: formatCents(resumo?.estimatedSavingsCents ?? 0),
      hint: 'Estimado × escolhido',
      tone: (resumo && resumo.estimatedSavingsCents < 0 ? 'ruim' : 'neutro') as 'ruim' | 'neutro',
    },
  ];

  const columns: Array<Column<Quotation>> = [
    { key: 'code', label: 'Código', mono: true, strong: true, render: (row) => row.code },
    { key: 'createdAt', label: 'Data', mono: true, render: (row) => formatDate(row.createdAt) },
    {
      key: 'requisitionCode',
      label: 'Requisição',
      mono: true,
      render: (row) => blank(row.requisitionCode),
    },
    {
      key: 'items',
      label: 'Descrição / itens',
      render: (row) => (
        <span>
          {row.items[0]?.description ?? '—'}
          <span style={{ display: 'block', fontSize: 11, color: T.inkFaint }}>
            {row.items.length === 1 ? '1 item' : `${row.items.length} itens`}
          </span>
        </span>
      ),
    },
    {
      key: 'supplierQuotes',
      label: 'Fornecedores',
      render: (row) => (
        <span style={{ display: 'inline-flex', gap: 3 }}>
          {row.supplierQuotes.slice(0, 3).map((quote) => (
            <span
              key={quote.id}
              className="avatarinho"
              title={`${quote.supplierName} · ${SUPPLIER_QUOTE_STATUS_LABELS[quote.status]}`}
              style={
                quote.status === 'PROPOSTA_RECEBIDA'
                  ? { background: T.mareTint, color: T.mareDark }
                  : undefined
              }
            >
              {iniciais(quote.supplierName).toUpperCase()}
            </span>
          ))}
          {row.supplierQuotes.length > 3 && (
            <span className="avatarinho">+{row.supplierQuotes.length - 3}</span>
          )}
          {row.supplierQuotes.length === 0 && <span style={{ color: T.inkFaint }}>—</span>}
        </span>
      ),
    },
    {
      key: 'dueDate',
      label: 'Prazo de resposta',
      mono: true,
      render: (row) => (row.dueDate ? formatDate(row.dueDate) : '—'),
    },
    {
      key: 'status',
      label: 'Situação',
      render: (row) => (
        <StatusBadge kind={STATUS_TONE[row.status]}>
          {QUOTATION_STATUS_LABELS[row.status]}
        </StatusBadge>
      ),
    },
    {
      key: 'vencedor',
      label: 'Vencedor',
      render: (row) => {
        const vencedores = new Set(
          row.items.map((item) => item.winnerSupplierName).filter(Boolean) as string[],
        );
        if (vencedores.size === 0) return <span style={{ color: T.inkFaint }}>—</span>;
        if (vencedores.size === 1) return [...vencedores][0];
        return (
          <span title={[...vencedores].join(', ')}>
            {vencedores.size} fornecedores
          </span>
        );
      },
    },
    {
      key: 'awardedTotalCents',
      label: 'Valor final',
      right: true,
      mono: true,
      render: (row) =>
        row.awardedTotalCents > 0 ? (
          formatCents(row.awardedTotalCents)
        ) : (
          <span style={{ color: T.inkFaint }}>—</span>
        ),
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Cotações"
        actions={
          can('cotacoes:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCriando(true)}>
              Nova cotação
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <SummaryCards cards={cards} loading={summaryQuery.isPending} />

        <FilterBar
          placeholder="Buscar por código, requisição, item ou fornecedor…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'status',
              label: 'Situação',
              value: status,
              options: QUOTATION_STATUSES.map((value) => ({
                value,
                label: QUOTATION_STATUS_LABELS[value],
              })),
              onChange: (value) => {
                setStatus(value);
                setPage(1);
              },
            },
          ]}
          activeMoreFilters={maisFiltrosAtivos}
          onClearMore={limparMais}
          moreFilters={
            <MaisFiltros
              personId={personId}
              costCenterId={costCenterId}
              from={from}
              to={to}
              awarded={awarded}
              onChange={(patch) => {
                if (patch.personId !== undefined) setPersonId(patch.personId);
                if (patch.costCenterId !== undefined) setCostCenterId(patch.costCenterId);
                if (patch.from !== undefined) setFrom(patch.from);
                if (patch.to !== undefined) setTo(patch.to);
                if (patch.awarded !== undefined) setAwarded(patch.awarded);
                setPage(1);
              }}
            />
          }
        />

        {listQuery.isPending && <TableSkeleton columns={9} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Cotações"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={(value) => {
              setPerPage(value);
              setPage(1);
            }}
            checkedIds={checked}
            onCheckedChange={setChecked}
            rowActions={(row) => (
              <button
                type="button"
                className="rowaction"
                aria-label={`Abrir ${row.code}`}
                onClick={() => setSelectedId(row.id)}
              >
                <MoreHorizontal size={15} aria-hidden />
              </button>
            )}
            emptyState={
              temFiltro ? (
                <NoResultsState onClear={limparTudo} />
              ) : (
                <EmptyState
                  title="Nenhuma cotação ainda"
                  description="A cotação nasce de uma requisição já aprovada."
                  action={
                    can('cotacoes:criar') ? (
                      <Button variant="primary" icon={Plus} onClick={() => setCriando(true)}>
                        Nova cotação
                      </Button>
                    ) : undefined
                  }
                />
              )
            }
          />
        )}

        <div className="note">
          <Info size={16} className="note__icon" aria-hidden />
          <div>
            <strong>Toda cotação nasce de uma requisição aprovada.</strong> Os itens e as
            quantidades vêm de lá, e o que for comprado dá baixa na linha certa — uma
            requisição pode virar mais de uma cotação quando vale separar os lotes.
          </div>
        </div>
      </div>

      <ContextPanel
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={quotation?.code ?? 'Cotação'}
        subtitle={quotation ? `requisição ${blank(quotation.requisitionCode)}` : undefined}
        tabs={['Resumo', 'Mapa comparativo', 'Fornecedores', 'Anexos', 'Histórico']}
        footer={
          quotation && (
            <Acoes
              quotation={quotation}
              can={can}
              onConvidar={() => setConvidando(true)}
              onEscolher={() => setEscolhendo(true)}
              onFinalizar={() => finalizeMutation.mutate(undefined)}
              onAprovar={() => approveMutation.mutate(undefined)}
              onCancelar={() => setCancelando(true)}
              trabalhando={finalizeMutation.isPending || approveMutation.isPending}
            />
          )
        }
      >
        {(aba) => {
          if (detailQuery.isPending) return <LoadingState label="Montando o comparativo…" />;
          if (!quotation) return null;

          if (aba === 1) return <MapaComparativo quotation={quotation} />;
          if (aba === 2)
            return (
              <AbaFornecedores
                quotation={quotation}
                canEdit={can('cotacoes:editar')}
                onPropor={setPropondo}
                onSemResposta={setSemResposta}
              />
            );
          if (aba === 3)
            return (
              <div>
                {quotation.supplierQuotes.length === 0 && (
                  <p className="muted" style={{ fontSize: 13 }}>
                    Convide um fornecedor para anexar a proposta comercial dele.
                  </p>
                )}
                {quotation.supplierQuotes.map((quote) => (
                  <div key={quote.id} style={{ marginBottom: 18 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                      {quote.supplierName}
                    </div>
                    <Attachments
                      entity="SupplierQuote"
                      entityId={quote.id}
                      canEdit={can('cotacoes:editar')}
                    />
                  </div>
                ))}
              </div>
            );
          if (aba === 4) return <AbaHistorico quotationId={quotation.id} />;
          return <AbaResumo quotation={quotation} />;
        }}
      </ContextPanel>

      {/* Montado só quando aberto: o formulário nasce limpo e não fica
          guardando estado de uma abertura anterior. */}
      {criando && (
      <NovaCotacaoModal
        open={criando}
        onClose={() => setCriando(false)}
        onCreated={(id) => {
          setCriando(false);
          setSelectedId(id);
          refresh();
        }}
      />
      )}

      {quotation && (
        <>
          <ConvidarModal
            open={convidando}
            quotation={quotation}
            onClose={() => setConvidando(false)}
            onDone={() => {
              setConvidando(false);
              refresh();
            }}
          />

          {propondo && (
            <PropostaDrawer
              key={propondo.id}
              quote={propondo}
              quotation={quotation}
              onClose={() => setPropondo(null)}
              onSaved={() => {
                setPropondo(null);
                refresh();
              }}
            />
          )}

          <EscolhaDrawer
            open={escolhendo}
            quotation={quotation}
            onClose={() => setEscolhendo(false)}
            onSaved={() => {
              setEscolhendo(false);
              refresh();
            }}
          />
        </>
      )}

      <ConfirmDialog
        open={Boolean(semResposta)}
        requireReason
        loading={answerMutation.isPending}
        title="Fornecedor não respondeu?"
        confirmLabel="Registrar"
        message={
          <>
            <strong>{semResposta?.supplierName}</strong> fica registrado como fora desta
            cotação. Ninguém precisa inventar uma proposta para o comparativo fechar.
            {answerMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(answerMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setSemResposta(null);
          answerMutation.reset();
        }}
        onConfirm={(reason) =>
          semResposta &&
          answerMutation.mutate({ id: semResposta.id, status: 'RECUSOU', reason: reason ?? null })
        }
      />

      <ConfirmDialog
        open={cancelando}
        destructive
        requireReason
        loading={cancelMutation.isPending}
        title="Cancelar esta cotação?"
        confirmLabel="Cancelar cotação"
        message="As propostas recebidas ficam no histórico com o motivo, e os itens voltam para a fila de Compras."
        onClose={() => {
          setCancelando(false);
          cancelMutation.reset();
        }}
        onConfirm={(reason) =>
          cancelMutation.mutate({ reason: reason ?? '' }, { onSuccess: () => setCancelando(false) })
        }
      />
    </>
  );
}

/* --------------------------------- filtros -------------------------------- */

function MaisFiltros({
  personId,
  costCenterId,
  from,
  to,
  awarded,
  onChange,
}: {
  personId: string;
  costCenterId: string;
  from: string;
  to: string;
  awarded: string;
  onChange: (
    patch: Partial<Record<'personId' | 'costCenterId' | 'from' | 'to' | 'awarded', string>>,
  ) => void;
}) {
  const suppliers = useSuppliers();
  const costCenters = useCostCenters();

  return (
    <>
      <label className="sr-only-wrap">
        <span className="sr-only">Fornecedor participante</span>
        <select
          className="filterbar__select"
          value={personId}
          onChange={(event) => onChange({ personId: event.target.value })}
        >
          <option value="">Fornecedor: todos</option>
          {(suppliers.data ?? []).map((supplier) => (
            <option key={supplier.id} value={supplier.id}>
              {supplier.tradeName || supplier.name}
            </option>
          ))}
        </select>
      </label>

      <label className="sr-only-wrap">
        <span className="sr-only">Centro de custo</span>
        <select
          className="filterbar__select"
          value={costCenterId}
          onChange={(event) => onChange({ costCenterId: event.target.value })}
        >
          <option value="">Centro de custo: todos</option>
          {(costCenters.data ?? []).map((center) => (
            <option key={center.id} value={center.id}>
              {center.code} · {center.name}
            </option>
          ))}
        </select>
      </label>

      <label className="sr-only-wrap">
        <span className="sr-only">Vencedor definido</span>
        <select
          className="filterbar__select"
          value={awarded}
          onChange={(event) => onChange({ awarded: event.target.value })}
        >
          <option value="">Vencedor: tanto faz</option>
          <option value="true">Já tem vencedor</option>
          <option value="false">Ainda sem vencedor</option>
        </select>
      </label>

      <label className="sr-only-wrap">
        <span className="sr-only">Aberta a partir de</span>
        <DateInput
          className="filterbar__select"
          value={from}
          onChange={(iso) => onChange({ from: iso })}
          aria-label="Aberta a partir de"
        />
      </label>

      <label className="sr-only-wrap">
        <span className="sr-only">Aberta até</span>
        <DateInput
          className="filterbar__select"
          value={to}
          onChange={(iso) => onChange({ to: iso })}
          aria-label="Aberta até"
        />
      </label>
    </>
  );
}

/* ---------------------------- painel de contexto -------------------------- */

function AbaResumo({ quotation }: { quotation: Quotation }) {
  return (
    <>
      <StatTiles
        items={[
          { label: 'Estimado', value: formatCents(quotation.estimatedTotalCents) },
          {
            label: 'Escolhido',
            value:
              quotation.awardedTotalCents > 0 ? formatCents(quotation.awardedTotalCents) : '—',
          },
          {
            label: 'Diferença',
            value:
              quotation.awardedTotalCents > 0 ? (
                <span style={{ color: quotation.savingsCents < 0 ? T.red : T.green }}>
                  {formatCents(quotation.savingsCents)}
                </span>
              ) : (
                '—'
              ),
          },
        ]}
      />

      {quotation.needsReapproval && (
        <div className="aviso">
          O valor escolhido passa da alçada que aprovou a requisição
          {quotation.approvedUpToCents !== null && ` (${formatCents(quotation.approvedUpToCents)})`}.
          Finalizar manda esta cotação para reaprovação em vez de liberar o pedido.
        </div>
      )}

      <ReadField
        label="Situação"
        value={
          <StatusBadge kind={STATUS_TONE[quotation.status]}>
            {QUOTATION_STATUS_LABELS[quotation.status]}
          </StatusBadge>
        }
      />
      <ReadField label="Requisição" value={blank(quotation.requisitionCode)} mono />
      <ReadField label="Centro de custo" value={blank(quotation.costCenterName)} />
      <ReadField label="Setor" value={blank(quotation.departmentName)} />
      <ReadField
        label="Prioridade"
        value={REQUISITION_PRIORITY_LABELS[quotation.requisitionPriority]}
      />
      <ReadField
        label="Prazo para respostas"
        value={quotation.dueDate ? formatDate(quotation.dueDate) : '—'}
        mono
      />
      <ReadField
        label="Fornecedores"
        value={`${quotation.respondedCount} de ${quotation.invitedCount} responderam`}
      />
      <ReadField label="Observações" value={blank(quotation.notes)} />

      {quotation.decisionReason && (
        <ReadField label="Justificativa da escolha" value={quotation.decisionReason} />
      )}
      {quotation.cancelReason && (
        <ReadField label="Cancelada porque" value={quotation.cancelReason} />
      )}
      {quotation.finalizedAt && (
        <ReadField label="Finalizada em" value={formatDateTime(quotation.finalizedAt)} mono />
      )}
    </>
  );
}

/**
 * O mapa de cotação: itens nas linhas, fornecedores nas colunas.
 *
 * Menor preço da linha em verde. É o destaque que responde à pergunta da
 * tela — e que deixa ver quando vale dividir a compra entre dois.
 */
function MapaComparativo({ quotation }: { quotation: Quotation }) {
  const propostas = quotation.supplierQuotes.filter((q) => q.status === 'PROPOSTA_RECEBIDA');

  if (propostas.length === 0) {
    return (
      <EmptyState
        title="Nenhuma proposta ainda"
        description="Registre o que cada fornecedor respondeu para o comparativo aparecer."
      />
    );
  }

  return (
    <div className="table-scroll">
      <table className="table">
        <caption className="sr-only">Mapa comparativo de preços por fornecedor</caption>
        <thead>
          <tr>
            <th style={{ minWidth: 180 }}>Item</th>
            <th className="is-right">Qtd.</th>
            {propostas.map((quote) => (
              <th key={quote.id} className="is-right" style={{ minWidth: 120 }}>
                {quote.supplierName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {quotation.items.map((linha) => (
            <tr key={linha.id}>
              <td className="is-strong">
                {linha.description}
                {linha.winnerSupplierName && (
                  <span style={{ display: 'block', fontSize: 11, color: T.green }}>
                    <Trophy size={10} aria-hidden /> {linha.winnerSupplierName}
                  </span>
                )}
              </td>
              <td className="is-right mono">
                {formatQty(linha.quantityMilli)} {linha.unit}
              </td>
              {propostas.map((quote) => {
                const celula = quote.items.find((i) => i.quotationItemId === linha.id);
                if (!celula) {
                  return (
                    <td key={quote.id} className="is-right mono" style={{ color: T.inkFaint }}>
                      não cotou
                    </td>
                  );
                }
                return (
                  <td
                    key={quote.id}
                    className="is-right mono"
                    style={
                      celula.isBestPrice
                        ? { color: T.green, fontWeight: 700, background: T.greenTint }
                        : undefined
                    }
                  >
                    {formatCents(celula.unitPriceCents)}
                    {celula.brand && (
                      <span style={{ display: 'block', fontSize: 10, color: T.inkFaint }}>
                        {celula.brand}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}

          <tr>
            <td className="is-strong">Subtotal</td>
            <td />
            {propostas.map((quote) => (
              <td key={quote.id} className="is-right mono">
                {formatCents(quote.itemsCents)}
              </td>
            ))}
          </tr>
          <tr>
            <td>Frete</td>
            <td />
            {propostas.map((quote) => (
              <td key={quote.id} className="is-right mono">
                {quote.freightCents > 0 ? formatCents(quote.freightCents) : '—'}
              </td>
            ))}
          </tr>
          <tr>
            <td>Desconto</td>
            <td />
            {propostas.map((quote) => (
              <td key={quote.id} className="is-right mono">
                {quote.discountCents > 0 ? `− ${formatCents(quote.discountCents)}` : '—'}
              </td>
            ))}
          </tr>
          <tr>
            <td className="is-strong">Valor final</td>
            <td />
            {propostas.map((quote) => (
              <td
                key={quote.id}
                className="is-right mono is-strong"
                style={quote.isLowestTotal ? { color: T.green } : undefined}
              >
                {formatCents(quote.totalCents)}
                {!quote.coversAllItems && (
                  <div style={{ marginTop: 4 }}>
                    <StatusBadge kind="warn">parcial</StatusBadge>
                  </div>
                )}
              </td>
            ))}
          </tr>
          <tr>
            <td>Prazo de entrega</td>
            <td />
            {propostas.map((quote) => (
              <td key={quote.id} className="is-right mono">
                {quote.deliveryDays === null ? '—' : `${quote.deliveryDays} dias`}
              </td>
            ))}
          </tr>
          <tr>
            <td>Condição de pagamento</td>
            <td />
            {propostas.map((quote) => (
              <td key={quote.id} className="is-right">
                {blank(quote.paymentTerms)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function AbaFornecedores({
  quotation,
  canEdit,
  onPropor,
  onSemResposta,
}: {
  quotation: Quotation;
  canEdit: boolean;
  onPropor: (quote: SupplierQuote) => void;
  onSemResposta: (quote: SupplierQuote) => void;
}) {
  if (quotation.supplierQuotes.length === 0) {
    return (
      <EmptyState
        title="Nenhum fornecedor convidado"
        description="Convide quem vai cotar — cada um responde a própria proposta."
      />
    );
  }

  const congelada = quotation.status === 'FINALIZADA' || quotation.status === 'CANCELADA';

  return (
    <>
      {quotation.supplierQuotes.map((quote) => (
        <div key={quote.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ fontSize: 13 }}>{quote.supplierName}</strong>
            <StatusBadge
              kind={
                quote.status === 'PROPOSTA_RECEBIDA'
                  ? 'ok'
                  : quote.status === 'RECUSOU'
                    ? 'bad'
                    : quote.status === 'SEM_RESPOSTA'
                      ? 'warn'
                      : 'mute'
              }
            >
              {SUPPLIER_QUOTE_STATUS_LABELS[quote.status]}
            </StatusBadge>
            <div style={{ flex: 1 }} />
            {quote.wonItemCount > 0 && (
              <span style={{ fontSize: 11.5, color: T.green }}>
                <Trophy size={11} aria-hidden /> {quote.wonItemCount} item(ns) ·{' '}
                {formatCents(quote.wonTotalCents)}
              </span>
            )}
          </div>

          {quote.status === 'PROPOSTA_RECEBIDA' && (
            <div className="mono" style={{ fontSize: 12, color: T.inkSoft, marginTop: 6 }}>
              {formatCents(quote.totalCents)}
              {quote.deliveryDays !== null && ` · ${quote.deliveryDays} dias`}
              {quote.paymentTerms && ` · ${quote.paymentTerms}`}
              {quote.validUntil && ` · vale até ${formatDate(quote.validUntil)}`}
            </div>
          )}
          {quote.declineReason && (
            <div style={{ fontSize: 12, color: T.inkFaint, marginTop: 6 }}>
              “{quote.declineReason}”
            </div>
          )}

          {canEdit && !congelada && (
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <Button small onClick={() => onPropor(quote)}>
                {quote.status === 'PROPOSTA_RECEBIDA' ? 'Substituir proposta' : 'Registrar proposta'}
              </Button>
              {quote.status === 'CONVIDADO' && (
                <Button small variant="ghost" icon={XCircle} onClick={() => onSemResposta(quote)}>
                  Não respondeu
                </Button>
              )}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

const ACAO_LABEL: Record<string, string> = {
  CREATE: 'Aberta',
  UPDATE: 'Alterada',
  APPROVE: 'Vencedor escolhido',
  DELETE: 'Cancelada',
};

function AbaHistorico({ quotationId }: { quotationId: string }) {
  const query = useQuery({
    queryKey: ['quotation', quotationId, 'timeline'],
    queryFn: () => api.get<RequisitionTimelineEntry[]>(`/quotations/${quotationId}/timeline`),
  });

  if (query.isPending) return <LoadingState label="Buscando o histórico…" />;
  if (query.isError) return <ErrorState message={errorMessage(query.error)} />;

  const entradas = query.data ?? [];
  if (entradas.length === 0) {
    return <p className="muted" style={{ fontSize: 13 }}>Nada registrado ainda.</p>;
  }

  return (
    <ol className="timeline">
      {entradas.map((entrada) => (
        <li key={entrada.id} className="timeline__item">
          <div className="timeline__head">
            <strong>{ACAO_LABEL[entrada.action] ?? entrada.action}</strong>
            <span className="mono">{formatDateTime(entrada.at)}</span>
          </div>
          <div className="timeline__by">{blank(entrada.userName)}</div>
          {entrada.reason && <div className="timeline__reason">“{entrada.reason}”</div>}
        </li>
      ))}
    </ol>
  );
}

function Acoes({
  quotation,
  can,
  onConvidar,
  onEscolher,
  onFinalizar,
  onAprovar,
  onCancelar,
  trabalhando,
}: {
  quotation: Quotation;
  can: (permission: string) => boolean;
  onConvidar: () => void;
  onEscolher: () => void;
  onFinalizar: () => void;
  onAprovar: () => void;
  onCancelar: () => void;
  trabalhando: boolean;
}) {
  const congelada = quotation.status === 'FINALIZADA' || quotation.status === 'CANCELADA';
  const temVencedor = quotation.awardedTotalCents > 0;

  return (
    <>
      {!congelada && can('cotacoes:editar') && (
        <Button small icon={UserPlus} onClick={onConvidar}>
          Convidar fornecedor
        </Button>
      )}
      {!congelada && quotation.respondedCount > 0 && can('cotacoes:aprovar') && (
        <Button small icon={Trophy} onClick={onEscolher}>
          Escolher vencedores
        </Button>
      )}
      {quotation.status === 'AGUARDANDO_ANALISE' && temVencedor && can('cotacoes:aprovar') && (
        <Button small variant="primary" loading={trabalhando} onClick={onFinalizar}>
          Finalizar
        </Button>
      )}
      {quotation.status === 'EM_APROVACAO' && can('cotacoes:aprovar') && (
        <Button small variant="primary" loading={trabalhando} onClick={onAprovar}>
          Aprovar o valor
        </Button>
      )}
      {!congelada && can('cotacoes:excluir') && (
        <Button small variant="danger" icon={Ban} onClick={onCancelar}>
          Cancelar
        </Button>
      )}
    </>
  );
}

/* ------------------------------- formulários ------------------------------ */

/**
 * Nova cotação: escolhe a requisição APROVADA e quais itens entram.
 *
 * A quantidade vem preenchida com o pendente da linha e pode ser reduzida —
 * é assim que uma requisição vira duas cotações.
 */
function NovaCotacaoModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [requisitionId, setRequisitionId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [marcados, setMarcados] = useState<Record<string, number | null>>({});
  const [carregadoDe, setCarregadoDe] = useState<string | null>(null);

  const chave = open ? requisitionId || 'vazio' : null;
  if (chave !== carregadoDe) {
    setCarregadoDe(chave);
    if (!open) {
      setRequisitionId('');
      setDueDate('');
    }
    setMarcados({});
  }

  // Só as aprovadas que ainda têm item esperando Compras — abrir cotação para
  // o que já foi todo comprado não faz sentido nenhum.
  const pendentes = useQuery({
    queryKey: ['purchase-requisitions', 'pendentes'],
    queryFn: () =>
      api.get<Paginated<Requisition>>('/purchase-requisitions', { pending: 'true', perPage: 100 }),
    enabled: open,
  });

  const requisition = (pendentes.data?.data ?? []).find((r) => r.id === requisitionId);
  const linhas = (requisition?.items ?? []).filter((item) => item.pendingMilli > 0);

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post<Quotation>('/quotations', body),
    onSuccess: (created) => onCreated(created.id),
  });
  const errors = fieldErrors(mutation.error);

  const escolhidos = linhas.filter((linha) => (marcados[linha.id] ?? 0) > 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nova cotação"
      description="Só requisições aprovadas com item pendente entram em cotação"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={escolhidos.length === 0}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                requisitionId,
                dueDate: dueDate || null,
                items: escolhidos.map((linha) => ({
                  requisitionItemId: linha.id,
                  quantityMilli: marcados[linha.id],
                })),
              })
            }
          >
            Abrir cotação
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Requisição aprovada" htmlFor="cot-req" error={errors.requisitionId}>
          <select
            id="cot-req"
            className="input"
            value={requisitionId}
            onChange={(event) => setRequisitionId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(pendentes.data?.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.code} · {r.costCenterName ?? ''} · {r.pendingItemCount} item(ns) pendente(s)
              </option>
            ))}
          </select>
        </Field>

        {pendentes.data?.data.length === 0 && (
          <p className="muted" style={{ fontSize: 12.5 }}>
            Nenhuma requisição aprovada com item pendente. Aprove uma em Compras › Requisições.
          </p>
        )}

        <Field label="Prazo para respostas" htmlFor="cot-due">
          <DateInput
            id="cot-due"
            className="input input--mono"
            min={todayISO()}
            value={dueDate}
            onChange={(iso) => setDueDate(iso)}
          />
        </Field>
      </FormSection>

      {requisitionId && (
        <FormSection
          title="Itens desta cotação"
          description="Marque o que entra agora. O que sobrar continua na fila de Compras."
        >
          {linhas.length === 0 && (
            <p className="muted" style={{ fontSize: 12.5 }}>
              Todos os itens desta requisição já foram cotados ou comprados.
            </p>
          )}
          {linhas.map((linha) => {
            const marcado = (marcados[linha.id] ?? 0) > 0;
            return (
              <div key={linha.id} className="card" style={{ padding: 10, marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={marcado}
                    onChange={() =>
                      setMarcados((atual) => ({
                        ...atual,
                        [linha.id]: marcado ? null : linha.pendingMilli,
                      }))
                    }
                  />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{linha.description}</span>
                  <span className="mono" style={{ fontSize: 11.5, color: T.inkFaint }}>
                    {formatQty(linha.pendingMilli)} {linha.unit} pendente(s)
                  </span>
                </label>

                {marcado && (
                  <div style={{ marginTop: 8 }}>
                    <Field label="Quantidade a cotar" htmlFor={`cot-qty-${linha.id}`}>
                      <QtyInput
                        id={`cot-qty-${linha.id}`}
                        value={marcados[linha.id] ?? null}
                        unit={linha.unit}
                        onChange={(value) =>
                          setMarcados((atual) => ({ ...atual, [linha.id]: value }))
                        }
                      />
                    </Field>
                  </div>
                )}
              </div>
            );
          })}
        </FormSection>
      )}
    </Modal>
  );
}

function ConvidarModal({
  open,
  quotation,
  onClose,
  onDone,
}: {
  open: boolean;
  quotation: Quotation;
  onClose: () => void;
  onDone: () => void;
}) {
  const suppliers = useSuppliers();
  const [personId, setPersonId] = useState('');
  const [contactName, setContactName] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setPersonId('');
      setContactName('');
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post(`/quotations/${quotation.id}/suppliers`, body),
    onSuccess: onDone,
  });

  const jaConvidados = new Set(quotation.supplierQuotes.map((q) => q.personId));
  const disponiveis = (suppliers.data ?? []).filter((s) => !jaConvidados.has(s.id));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Convidar fornecedor"
      description="Ele entra como convidado, sem proposta — o preço vem depois"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!personId}
            loading={mutation.isPending}
            onClick={() => mutation.mutate({ personId, contactName: contactName.trim() || null })}
          >
            Convidar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Fornecedor" htmlFor="conv-person">
          <select
            id="conv-person"
            className="input"
            value={personId}
            onChange={(event) => setPersonId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {disponiveis.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.tradeName || supplier.name}
              </option>
            ))}
          </select>
        </Field>
        {disponiveis.length === 0 && (
          <p className="muted" style={{ fontSize: 12.5 }}>
            Todos os fornecedores cadastrados já estão nesta cotação.
          </p>
        )}

        <Field label="Contato / vendedor" htmlFor="conv-contact" hint="Quem atendeu do lado de lá">
          <input
            id="conv-contact"
            className="input"
            value={contactName}
            onChange={(event) => setContactName(event.target.value)}
            placeholder="Marcos, do balcão"
          />
        </Field>
      </FormSection>
    </Modal>
  );
}

/**
 * Registro da proposta de UM fornecedor.
 *
 * Substituir não apaga a anterior: ela fica no histórico da cotação. Deixar a
 * linha em branco significa que o fornecedor não cotou aquele item — atender
 * parcialmente é resposta legítima.
 */
function PropostaDrawer({
  quote,
  quotation,
  onClose,
  onSaved,
}: {
  quote: SupplierQuote;
  quotation: Quotation;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [quotedAt, setQuotedAt] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [deliveryDays, setDeliveryDays] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [freightCents, setFreightCents] = useState<number | null>(null);
  const [discountCents, setDiscountCents] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [linhas, setLinhas] = useState<
    Record<string, { preco: number | null; qtd: number | null; marca: string }>
  >({});
  const [carregadoDe, setCarregadoDe] = useState<string | null>(null);

  // Carrega a proposta existente uma vez só, ao abrir para este fornecedor.
  //
  // A chave é normalizada para `null` antes de comparar. Com `quote?.id`
  // cru, o caso fechado dava `undefined !== null` a cada render — verdadeiro
  // para sempre — e o React estourava em "too many re-renders". Já aconteceu
  // uma vez no cadastro de item pela nota; é o mesmo laço.
  if (quote.id !== carregadoDe) {
    setCarregadoDe(quote.id);
    {
      setQuotedAt(quote.quotedAt ?? todayISO());
      setValidUntil(quote.validUntil ?? '');
      setDeliveryDays(quote.deliveryDays === null ? '' : String(quote.deliveryDays));
      setPaymentTerms(quote.paymentTerms ?? '');
      setFreightCents(quote.freightCents || null);
      setDiscountCents(quote.discountCents || null);
      setNotes(quote.notes ?? '');
      setLinhas(
        Object.fromEntries(
          quotation.items.map((linha) => {
            const anterior = quote.items.find((i) => i.quotationItemId === linha.id);
            return [
              linha.id,
              {
                preco: anterior?.unitPriceCents ?? null,
                qtd: anterior?.quantityMilli ?? linha.quantityMilli,
                marca: anterior?.brand ?? '',
              },
            ];
          }),
        ),
      );
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.put(`/quotations/${quotation.id}/supplier-quotes`, body),
    onSuccess: onSaved,
  });

  const cotadas = quotation.items.filter((linha) => (linhas[linha.id]?.preco ?? 0) > 0);
  const itens = cotadas.reduce(
    (soma, linha) =>
      soma +
      Math.round(((linhas[linha.id]?.qtd ?? 0) * (linhas[linha.id]?.preco ?? 0)) / 1000),
    0,
  );
  const total = itens + (freightCents ?? 0) - (discountCents ?? 0);

  return (
    <Drawer
      open
      onClose={onClose}
      title={`Proposta de ${quote.supplierName}`}
      description="Deixe em branco o item que o fornecedor não cotou"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={cotadas.length === 0}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                personId: quote.personId,
                quotedAt: quotedAt || null,
                validUntil: validUntil || null,
                deliveryDays: deliveryDays === '' ? null : Number(deliveryDays),
                paymentTerms: paymentTerms.trim() || null,
                contactName: quote.contactName,
                freightCents: freightCents ?? 0,
                discountCents: discountCents ?? 0,
                notes: notes.trim() || null,
                items: cotadas.map((linha) => ({
                  quotationItemId: linha.id,
                  quantityMilli: linhas[linha.id]?.qtd ?? linha.quantityMilli,
                  unitPriceCents: linhas[linha.id]?.preco ?? 0,
                  brand: linhas[linha.id]?.marca.trim() || null,
                })),
              })
            }
          >
            Salvar proposta
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <div className="grid-2">
          <Field label="Data da proposta" htmlFor="prop-date">
            <DateInput
              id="prop-date"
              className="input input--mono"
              value={quotedAt}
              onChange={(iso) => setQuotedAt(iso)}
            />
          </Field>
          <Field label="Válida até" htmlFor="prop-valid">
            <DateInput
              id="prop-valid"
              className="input input--mono"
              value={validUntil}
              onChange={(iso) => setValidUntil(iso)}
            />
          </Field>
        </div>

        <div className="grid-2">
          <Field label="Prazo de entrega (dias)" htmlFor="prop-days">
            <input
              id="prop-days"
              className="input input--mono"
              inputMode="numeric"
              value={deliveryDays}
              onChange={(event) => setDeliveryDays(event.target.value.replace(/\D/g, ''))}
            />
          </Field>
          <Field label="Condição de pagamento" htmlFor="prop-terms">
            <input
              id="prop-terms"
              className="input"
              value={paymentTerms}
              onChange={(event) => setPaymentTerms(event.target.value)}
              placeholder="30/60 dias"
            />
          </Field>
        </div>

        <div className="grid-2">
          <Field label="Frete" htmlFor="prop-freight">
            <MoneyInput id="prop-freight" value={freightCents} onChange={setFreightCents} />
          </Field>
          <Field label="Desconto" htmlFor="prop-discount">
            <MoneyInput id="prop-discount" value={discountCents} onChange={setDiscountCents} />
          </Field>
        </div>

        <Field label="Observações" htmlFor="prop-notes">
          <textarea
            id="prop-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
      </FormSection>

      <FormSection title="Preços por item">
        {quotation.items.map((linha) => (
          <div key={linha.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{linha.description}</div>
            <div className="mono" style={{ fontSize: 11.5, color: T.inkFaint, marginBottom: 8 }}>
              pedido: {formatQty(linha.quantityMilli)} {linha.unit}
            </div>

            <div className="grid-2">
              <Field label="Preço unitário" htmlFor={`prop-price-${linha.id}`}>
                <MoneyInput
                  id={`prop-price-${linha.id}`}
                  value={linhas[linha.id]?.preco ?? null}
                  onChange={(value) =>
                    setLinhas((atual) => ({
                      ...atual,
                      [linha.id]: {
                        preco: value,
                        qtd: atual[linha.id]?.qtd ?? linha.quantityMilli,
                        marca: atual[linha.id]?.marca ?? '',
                      },
                    }))
                  }
                />
              </Field>
              <Field
                label="Quantidade ofertada"
                htmlFor={`prop-qty-${linha.id}`}
                hint="Menos que o pedido é aceito"
              >
                <QtyInput
                  id={`prop-qty-${linha.id}`}
                  value={linhas[linha.id]?.qtd ?? linha.quantityMilli}
                  unit={linha.unit}
                  onChange={(value) =>
                    setLinhas((atual) => ({
                      ...atual,
                      [linha.id]: {
                        preco: atual[linha.id]?.preco ?? null,
                        qtd: value,
                        marca: atual[linha.id]?.marca ?? '',
                      },
                    }))
                  }
                />
              </Field>
            </div>

            <Field label="Marca / modelo" htmlFor={`prop-brand-${linha.id}`}>
              <input
                id={`prop-brand-${linha.id}`}
                className="input"
                value={linhas[linha.id]?.marca ?? ''}
                onChange={(event) =>
                  setLinhas((atual) => ({
                    ...atual,
                    [linha.id]: {
                      preco: atual[linha.id]?.preco ?? null,
                      qtd: atual[linha.id]?.qtd ?? linha.quantityMilli,
                      marca: event.target.value,
                    },
                  }))
                }
              />
            </Field>
          </div>
        ))}

        <p className="mono" style={{ fontSize: 13 }}>
          Itens: {formatCents(itens)} · Total desta proposta: <strong>{formatCents(total)}</strong>
        </p>
      </FormSection>
    </Drawer>
  );
}

/**
 * A escolha, linha a linha.
 *
 * O menor preço de cada linha vem marcado, mas quem decide é o comprador —
 * e escolher fora do menor preço exige justificativa, que o servidor cobra.
 */
function EscolhaDrawer({
  open,
  quotation,
  onClose,
  onSaved,
}: {
  open: boolean;
  quotation: Quotation;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [escolhas, setEscolhas] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setReason(quotation.decisionReason ?? '');
      setEscolhas(
        Object.fromEntries(
          quotation.supplierQuotes.flatMap((quote) =>
            quote.items.filter((i) => i.isWinner).map((i) => [i.quotationItemId, quote.id]),
          ),
        ),
      );
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post(`/quotations/${quotation.id}/award`, body),
    onSuccess: onSaved,
  });

  const propostas = quotation.supplierQuotes.filter((q) => q.status === 'PROPOSTA_RECEBIDA');

  // Alguma escolha fora do menor preço? O servidor decide de novo, mas o
  // operador merece saber antes de clicar.
  const foraDoMenor = quotation.items.filter((linha) => {
    const escolhida = escolhas[linha.id];
    if (!escolhida) return false;
    const oferta = propostas
      .find((q) => q.id === escolhida)
      ?.items.find((i) => i.quotationItemId === linha.id);
    return Boolean(oferta && linha.bestUnitPriceCents !== null && oferta.unitPriceCents > linha.bestUnitPriceCents);
  });

  const total = quotation.items.reduce((soma, linha) => {
    const escolhida = escolhas[linha.id];
    const oferta = propostas
      .find((q) => q.id === escolhida)
      ?.items.find((i) => i.quotationItemId === linha.id);
    return soma + (oferta?.totalCents ?? 0);
  }, 0);

  const precisaMotivo = foraDoMenor.length > 0 && reason.trim().length < 10;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Escolher vencedores"
      description="Cada item pode ir para um fornecedor diferente"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={Object.keys(escolhas).length === 0 || precisaMotivo}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                awards: Object.entries(escolhas).map(([quotationItemId, supplierQuoteId]) => ({
                  quotationItemId,
                  supplierQuoteId,
                })),
                reason: reason.trim() || null,
              })
            }
          >
            Confirmar escolha
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        {quotation.items.map((linha) => (
          <div key={linha.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{linha.description}</div>
            <div className="mono" style={{ fontSize: 11.5, color: T.inkFaint, marginBottom: 8 }}>
              {formatQty(linha.quantityMilli)} {linha.unit}
            </div>

            {propostas.map((quote) => {
              const oferta = quote.items.find((i) => i.quotationItemId === linha.id);
              if (!oferta) return null;
              const melhor = oferta.unitPriceCents === linha.bestUnitPriceCents;
              return (
                <label
                  key={quote.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '5px 0',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name={`escolha-${linha.id}`}
                    checked={escolhas[linha.id] === quote.id}
                    onChange={() => setEscolhas((atual) => ({ ...atual, [linha.id]: quote.id }))}
                  />
                  <span style={{ fontSize: 12.5, flex: 1 }}>{quote.supplierName}</span>
                  <span
                    className="mono"
                    style={{ fontSize: 12.5, color: melhor ? T.green : T.inkSoft, fontWeight: melhor ? 700 : 400 }}
                  >
                    {formatCents(oferta.unitPriceCents)}
                  </span>
                  {melhor && <StatusBadge kind="ok">menor</StatusBadge>}
                </label>
              );
            })}

            {propostas.every((q) => !q.items.some((i) => i.quotationItemId === linha.id)) && (
              <p className="muted" style={{ fontSize: 12 }}>
                Nenhum fornecedor cotou este item.
              </p>
            )}
          </div>
        ))}

        <p className="mono" style={{ fontSize: 13 }}>
          Total escolhido: <strong>{formatCents(total)}</strong>
        </p>

        {foraDoMenor.length > 0 && (
          <>
            <div className="aviso">
              {foraDoMenor.length === 1
                ? `"${foraDoMenor[0]?.description}" não é o menor preço.`
                : `${foraDoMenor.length} itens não são o menor preço.`}{' '}
              A justificativa é obrigatória e fica registrada com o seu nome.
            </div>
            <Field label="Justificativa" htmlFor="escolha-reason">
              <textarea
                id="escolha-reason"
                className="input"
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Prazo de entrega, marca homologada, frete incluso, garantia…"
              />
            </Field>
          </>
        )}
      </FormSection>
    </Drawer>
  );
}
